/**
 * Header-card chips with hover/focus popovers — NEW surfaces transcribed
 * from exports 'Pre-screen (7)/(8)/(9).png': the chip goes SOLID while open
 * and a small white panel lists vehicle registration numbers (legacy plate /
 * make+model fallback) / per-pet weights / per-child ages. Data comes ONLY
 * from the typed property step (data-map §1); absent fields degrade per row
 * exactly like the Household card.
 */
import { Baby, Car, PawPrint } from 'lucide-react'
import { type ReactNode, useRef, useState } from 'react'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '#app/components/ui/popover'
import {
	childAgeLabel,
	countLabel,
	formatPetWeight,
	petTypeLabel,
	vehicleTypeLabel,
	vehicleValueLabel,
} from '#app/utils/application-detail-v2/display'
import type { ParsedStepData } from '#app/utils/application-detail-v2/types'
import { useFormatDate } from '#app/utils/application-detail-v2/vendor/use-format-date'
import { cn } from '#app/utils/misc'
import { petTypeIcon, vehicleTypeIcon } from './household-icons'
import { CHIP_TONES } from './primitives'

const SOLID_TONES: Record<'green' | 'rose' | 'purple', string> = {
	green: 'bg-[#A5BB83] text-white',
	rose: 'bg-[#BE7B97] text-white',
	purple: 'bg-[#9976B8] text-white',
}

/** Chip + popover that opens on hover/focus (no Radix hover-card in the
 * repo — a controlled Popover with pointer/focus handlers stands in). */
function HoverBadge({
	tone,
	icon,
	label,
	children,
}: {
	tone: keyof typeof SOLID_TONES
	icon: ReactNode
	label: string
	children: ReactNode
}) {
	const [open, setOpen] = useState(false)
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const triggerRef = useRef<HTMLButtonElement>(null)

	const clearTimer = () => {
		if (timerRef.current) {
			clearTimeout(timerRef.current)
			timerRef.current = null
		}
	}

	const handleOpen = () => {
		clearTimer()
		setOpen(true)
	}

	const handleClose = () => {
		clearTimer()
		timerRef.current = setTimeout(() => {
			setOpen(false)
		}, 200)
	}

	return (
		<Popover
			open={open}
			onOpenChange={(newOpen) => {
				if (!newOpen && open) {
					handleClose()
				} else if (newOpen) {
					handleOpen()
				}
			}}
		>
			<PopoverTrigger
				ref={triggerRef}
				onMouseEnter={handleOpen}
				onMouseLeave={handleClose}
				onFocus={handleOpen}
				onBlur={handleClose}
				className={cn(
					'inline-flex cursor-default cursor-pointer items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors [&>svg]:size-4',
					open ? SOLID_TONES[tone] : CHIP_TONES[tone],
				)}
			>
				{icon}
				{label}
			</PopoverTrigger>
			<PopoverContent
				align="start"
				sideOffset={8}
				onMouseEnter={() => {
					clearTimer()
					setOpen(true)
				}}
				onMouseLeave={handleClose}
				onOpenAutoFocus={(event) => event.preventDefault()}
				onCloseAutoFocus={(event) => event.preventDefault()}
				className="w-auto min-w-52 rounded-2xl border-0 bg-[#FBFDFD] p-3 shadow-[0_24px_60px_-12px_rgba(0,0,0,0.18)]"
			>
				<div className="flex flex-col gap-2.5">{children}</div>
			</PopoverContent>
		</Popover>
	)
}

function PopoverRow({
	icon,
	iconClass,
	label,
	children,
}: {
	icon: ReactNode
	iconClass: string
	label?: string
	children: ReactNode
}) {
	return (
		<div className="rounded-xl border border-[#F0F0F0] px-3.5 py-2.5">
			{label ? <p className="mb-1 text-xs text-gray-500">{label}</p> : null}
			<div className="flex items-center gap-2.5">
				<span className={cn('shrink-0 [&>svg]:size-4', iconClass)}>{icon}</span>
				{children}
			</div>
		</div>
	)
}

export function HeaderBadges({
	propertyStep,
}: {
	propertyStep: ParsedStepData['property']
}) {
	const { formatShortDate } = useFormatDate()
	const children = propertyStep?.children ?? []
	const pets = propertyStep?.pets ?? []
	const vehicles = propertyStep?.vehicles ?? []
	if (children.length + pets.length + vehicles.length === 0) return null

	return (
		<div className="mt-3 flex flex-wrap items-center gap-2.5">
			{children.length > 0 ? (
				<HoverBadge
					tone="purple"
					icon={<Baby />}
					label={countLabel(children.length, 'Child', 'Children')}
				>
					{children.map((child, index) => {
						const age = childAgeLabel(child.dateOfBirth, new Date())
						return (
							<PopoverRow
								// Static loader data — index keys are stable here.
								key={`child-${index}`}
								icon={<Baby />}
								iconClass="text-[#9976B8]"
								label={`Child ${index + 1}`}
							>
								<span className="text-sm font-semibold text-[#32302F]">
									Age : {age ?? child.name ?? '-'}
								</span>
								{child.dateOfBirth ? (
									<span className="text-xs text-[#747474]">
										({formatShortDate(child.dateOfBirth)})
									</span>
								) : null}
							</PopoverRow>
						)
					})}
				</HoverBadge>
			) : null}
			{pets.length > 0 ? (
				<HoverBadge
					tone="rose"
					icon={<PawPrint />}
					label={countLabel(pets.length, 'Pet', 'Pets')}
				>
					{pets.map((pet, index) => {
						const typeLabel = petTypeLabel(pet.petType) ?? pet.breed
						return (
							<PopoverRow
								key={`pet-${index}`}
								icon={petTypeIcon(pet.petType)}
								iconClass="text-[#BE7B97]"
							>
								<span className="text-sm font-semibold text-[#32302F]">
									{formatPetWeight(pet.weight) ?? '-'}
								</span>
								{typeLabel ? (
									<span className="text-xs text-[#747474]">({typeLabel})</span>
								) : null}
							</PopoverRow>
						)
					})}
				</HoverBadge>
			) : null}
			{vehicles.length > 0 ? (
				<HoverBadge
					tone="green"
					icon={<Car />}
					label={countLabel(vehicles.length, 'Vehicle', 'Vehicles')}
				>
					{vehicles.map((vehicle, index) => (
						<PopoverRow
							key={`vehicle-${index}`}
							icon={vehicleTypeIcon(vehicle.vehicleType)}
							iconClass="text-[#A5BB83]"
						>
							<span className="text-sm font-semibold text-[#32302F]">
								{vehicleValueLabel(vehicle) ?? '-'}
							</span>
							{vehicleTypeLabel(vehicle.vehicleType) ? (
								<span className="text-xs text-[#747474]">
									({vehicleTypeLabel(vehicle.vehicleType)})
								</span>
							) : null}
						</PopoverRow>
					))}
				</HoverBadge>
			) : null}
		</div>
	)
}
